import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import cn from 'classnames';
import { useMemo } from 'react';

import { getMenuIcon } from '../../utils/getMenuIcon';

const NavigationIcon = ({
  icon,
  isSelected,
}: {
  icon: string;
  isSelected: boolean;
}) => {
  const iconClass = cn(
    'h-4 w-4',
    isSelected
      ? 'text-color-ref-ui-icon-strong'
      : 'text-color-ref-ui-icon-disabled',
  );
  const Icon = getMenuIcon(icon);
  return <Icon className={iconClass} />;
};

type NavigationSubItem = {
  title: string;
  id: string;
  onClick: () => void;
};

export type NavigationItem = {
  title: string;
  id: string;
  onClick: () => void;
  elements?: NavigationSubItem[];
  icon?: string;
};

const NavigationMenuItem = ({
  isMain = false,
  item,
  selectedID,
  errorIDs,
  completedIDs,
}: {
  isMain?: boolean;
  item: NavigationItem;
  selectedID: string;
  errorIDs: string[];
  completedIDs: string[];
}) => {
  const elementsIDs = item.elements?.map(({ id }) => id);
  const isSelected =
    item.id === selectedID || !!elementsIDs?.find((id) => id === selectedID);

  const isCompleted =
    completedIDs.includes(item.id) ||
    elementsIDs?.every((id) => completedIDs.includes(id));
  const isError =
    errorIDs.includes(item.id) ||
    elementsIDs?.every((id) => errorIDs.includes(id));
  const isDefault = !isCompleted && !isError;

  const hasOnClick = useMemo(
    () => !isMain || (isMain && !item.elements?.length),
    [isMain, item.elements],
  );

  return (
    <div className={cn('flex flex-col gap-2', isMain && 'mt-2')}>
      <div
        className={cn(
          'group/content flex items-center gap-2',
          !isMain && 'hover:cursor-pointer',
        )}
        onClick={hasOnClick ? item.onClick : undefined}
        data-testid={hasOnClick ? 'navigation-menu-item' : undefined}
      >
        <div
          className={cn(
            'flex h-8 w-8 min-h-8 min-w-8 justify-center rounded-full',
            isMain &&
              cn(
                'items-center border-[1.5px]', // design spec: 1.5px border on nav step circles

                isSelected && isCompleted && 'border-color-sys-algae-green-50',
                isSelected && !isCompleted && 'border-color-ref-ui-icon-strong',
                !isSelected && isCompleted && 'border-color-sys-algae-green-60',
                !isSelected &&
                  !isCompleted &&
                  'border-color-ref-ui-icon-disabled',
              ),
          )}
        >
          {isMain ? (
            isCompleted ? (
              <Icon
                name="check"
                title="Check"
                width="18px"
                className={cn(
                  isSelected
                    ? 'text-color-sys-algae-green-50'
                    : 'text-color-sys-algae-green-60',
                )}
              />
            ) : (
              item.icon && (
                <NavigationIcon icon={item.icon} isSelected={isSelected} />
              )
            )
          ) : null}
          {!isMain && (
            <div
              className={cn(
                'h-full w-0.5',
                isDefault &&
                  !isSelected &&
                  'bg-color-ref-ui-icon-disabled group-hover/content:bg-color-ref-ui-icon-strong',
                isDefault && isSelected && 'bg-color-ref-ui-icon-strong',
                isCompleted &&
                  !isSelected &&
                  'bg-color-sys-algae-green-60 group-hover/content:bg-color-sys-algae-green-50',
                isCompleted && isSelected && 'bg-color-sys-algae-green-50',
                isError &&
                  !isSelected &&
                  'bg-color-sys-lobster-red-60 group-hover/content:bg-color-sys-lobster-red-50',
                isError && isSelected && 'bg-color-sys-lobster-red-50',
              )}
            />
          )}
        </div>
        <div>
          <Text
            className={cn(
              isSelected && !isMain && 'font-bold',
              isDefault && !isSelected && 'text-color-ref-text-muted',
              isDefault &&
                !isSelected &&
                !isMain &&
                'group-hover/content:text-color-ref-ui-icon-strong group-hover/content:font-bold',
              isCompleted &&
                !isSelected &&
                'text-color-sys-algae-green-60 group-hover/content:text-color-sys-algae-green-50 group-hover/content:font-bold',
              isCompleted && isSelected && 'text-color-sys-algae-green-50',
              !isMain &&
                isError &&
                !isSelected &&
                'text-color-sys-lobster-red-60 group-hover/content:text-color-sys-lobster-red-50 group-hover/content:font-bold',
              !isMain &&
                isError &&
                isSelected &&
                'text-color-sys-lobster-red-50',
            )}
            variant={isMain ? 'headingTiny' : 'bodyRegular'}
          >
            {item.title}
          </Text>
        </div>
      </div>
      {isMain &&
        item.elements?.map((subItem) => (
          <NavigationMenuItem
            key={`${subItem.id}_${subItem.title}`}
            item={subItem}
            selectedID={selectedID}
            completedIDs={completedIDs}
            errorIDs={errorIDs}
          />
        ))}
    </div>
  );
};

type Props = {
  title: string;
  navigationItems: NavigationItem[];
  selectedID?: string;
  errorIDs?: string[];
  completedIDs?: string[];
};

export function NavigationMenu({
  title,
  navigationItems,
  selectedID = '',
  completedIDs = [],
  errorIDs = [],
}: Props) {
  return (
    <>
      <Text variant="headingTiny" className="mb-6 mt-8">
        {title}
      </Text>
      {navigationItems.map((item) => {
        return (
          <NavigationMenuItem
            key={`${item.id}_${item.title}`}
            item={item}
            isMain
            selectedID={selectedID}
            completedIDs={completedIDs}
            errorIDs={errorIDs}
          />
        );
      })}
    </>
  );
}
